/**
 * Orderings offered by the promotion Overview tab.
 *
 * Field and direction are COMBINED into one option rather than exposed as a field selector
 * plus a direction toggle. An operator arrives with a question — "which campaigns are being
 * used most", "what did we launch recently" — not with a column and a direction. The split
 * form also admits combinations nobody wants (name, descending) and doubles the states the
 * screen has to hold and test for no gain.
 *
 * `Newest first` sends CreatedAt/descending EXPLICITLY rather than omitting `orderBy` and
 * relying on the server's default. Both produce the same rows today. Omitting is what an
 * un-updated client does, and it means "no preference" — so if the service's default ever
 * changed, this option's label would quietly stop describing what it shows. An option that
 * names an order should ask for that order.
 *
 * There is deliberately no money ordering. Rows are per-currency and minor-unit scale differs
 * between them, so one sorted column would rank 1000 IRR above 5.00 USD by comparing integers
 * that mean different things. The contract does not offer it either.
 */
export type PromotionStatsOrderBy =
  | 'AppliedCount'
  | 'InFlightCount'
  | 'Name'
  | 'CreatedAt'

export interface PromotionSortOption {
  key: string
  label: string
  orderBy: PromotionStatsOrderBy
  descending: boolean
}

/**
 * The FIRST option is the default, and on a performance view that has to be a performance
 * measure. It was "Newest first", which sorts by creation date and so puts the promotions
 * that have done nothing yet — the newest ones — above the ones an operator opened this
 * screen to look at. On a tenant with a dozen recent promotions the entire first page was
 * zeros. "Newest first" is still here; it is just no longer what you get without asking.
 */
export const PROMOTION_SORT_OPTIONS: PromotionSortOption[] = [
  {
    key: 'mostApplied',
    label: 'Most applied',
    orderBy: 'AppliedCount',
    descending: true
  },
  {
    key: 'newest',
    label: 'Newest first',
    orderBy: 'CreatedAt',
    descending: true
  },
  {
    key: 'oldest',
    label: 'Oldest first',
    orderBy: 'CreatedAt',
    descending: false
  },
  {
    key: 'mostInFlight',
    label: 'Most in flight',
    orderBy: 'InFlightCount',
    descending: true
  },
  { key: 'name', label: 'Name (A–Z)', orderBy: 'Name', descending: false }
]

export const DEFAULT_PROMOTION_SORT = PROMOTION_SORT_OPTIONS[0]

export function promotionSortFor(key: string): PromotionSortOption {
  return (
    PROMOTION_SORT_OPTIONS.find((o) => o.key === key) ?? DEFAULT_PROMOTION_SORT
  )
}
